<template>
  <div class="px-2">
    <form class="py-2 tickets-cart-grid lg:gap-3 gap-5 gap-y-2 text-vp-base-900">
      <template
        v-for="item in items"
        :key="item.itemId"
      >
        <div class="col-span-2 flex flex-col flex-grow justify-start items-start text-base">
          <div>
            <span>{{ item.name }}</span>
            <span class="pl-1 text-vp-base-500">{{ '(' + formatCurrency(item.breakdown?.basePrice ?? 0, { currency: props.currency }) + ')' }}</span>
          </div>
          <span v-if="item.seatingInfo">{{ getItemSeatInfo(item) }}</span>
        </div>

        <!-- Editable input or read-only display based on readOnly prop -->
        <div
          v-if="!readOnly && item.itemType != ShoppingCartItemTypeEnum.SeatTicket"
          class="items-end py-1"
        >
          <ButtonsNumericInput
            :value="item.count ?? 0"
            :disabled="disabled"
            size="xxs"
            font-class="text-sm"
            @set="(num: number) => setTicketAmount(item, num)"
            @add="(delta: number) => addTicketAmount(item, delta)"
          />
        </div>

        <div
          v-else
          class="items-end justify-end"
        >
          <template v-if="item.count">
            {{ 'x' + item.count }}
          </template>
        </div>

        <div class="flex justify-end gap-1 flex-shrink-0">
          <div class="text-base">
            {{ formatCurrency(item.breakdown?.price ?? 0, { currency: props.currency }) }}
          </div>

          <VPButton
            v-if="!readOnly && item.itemType != ShoppingCartItemTypeEnum.SeatTicket"
            :disabled="changesDisabled"
            variant="text"
            class="shrink items-start -my-1.5 -mx-4"
            :title="itemRemoveDescription(item)"
            @click="onTicketRemove(item)"
          >
            <iconify-icon
              inline
              icon="mdi:close-circle"
              class="text-xl fill-vp-primary-600 w-5 h-5"
            />
            <Accessible :description="itemRemoveDescription(item)" />
          </VPButton>
        </div>
      </template>
    </form>

    <div class="pt-2 mb-2 flex items-center justify-between text-base font-bold border-t">
      <span>
        {{
          $ngettext('Cart (%{count} ticket):', 'Cart (%{count} tickets):', controlQuantity, {
            count: '' + controlQuantity,
          })
        }}
      </span>
      <span>
        {{ formatCurrency(priceBreakdown?.itemsTotal || 0, { currency: props.currency }) }}
      </span>
    </div>

    <div
      v-if="totalFeesAmount != 0"
      class="my-2 sm:my-4"
    >
      <div
        class="flex justify-between text-vp-base-500 font-bold"
        @click="toggleTaxesOpen"
      >
        <div class="flex justify-start items-center cursor-pointer">
          <div class="text-sm">
            {{ $gettext('Fees & Taxes') }}
          </div>
          <iconify-icon
            icon="mdi:chevron-down"
            class="transition-transform"
            :class="{ 'rotate-180': taxesOpen }"
          />
        </div>
        <span>{{ formatCurrency(totalFeesAmount, { currency: props.currency }) }}</span>
      </div>

      <div
        v-show="taxesOpen"
        class="text-sm text-vp-base-500"
      >
        <div
          v-for="(item, index) in itemsBreakdown"
          :key="index"
          class="flex items-center justify-between"
        >
          <span>{{ item.label }}</span>
          <span>{{ formatCurrency(item.amount, { currency: props.currency }) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useCheckoutStore } from './stores/checkout';
import { formatCurrency } from '@vp/common/util';
import ButtonsNumericInput from './components/ButtonsNumericInput.vue';
import VPButton from './components/VPButton.vue';
import { Accessible } from '@vp/vue-components';
import { Checkout } from '@vp/common';
import { useGettext } from 'vue3-gettext';
import { isVPError } from './types/VPError';
import { useToast } from 'vue-toastification';
import { ShoppingCartItemTypeEnum } from '@vp/common/generated/checkout.graphql';

const { $gettext, $ngettext } = useGettext();

type ShoppingCartItem = Checkout.ShoppingCartItem.ShoppingCartItem;

type Props = {
  readOnly?: boolean;
  items: ShoppingCartItem[];
  controlQuantity: number;
  priceBreakdown: Checkout.PriceBreakdown.PriceBreakdown;
  currency: string;
};

const props = defineProps<Props>();
const toast = useToast();

const checkoutStore = useCheckoutStore();

const totalFeesAmount = computed(() => {
  if (!props.priceBreakdown || !Array.isArray(props.priceBreakdown.itemsBreakdown)) {
    return 0;
  }

  const itemsBreakdown = props.priceBreakdown.itemsBreakdown;

  return itemsBreakdown.reduce((sum, item) => {
    return sum + item.amount;
  }, 0);
});

const itemsBreakdown = computed(() => (props.priceBreakdown.itemsBreakdown || []).filter(({ amount }) => amount != 0));

// Methods
const getItemSeatInfo = (item: ShoppingCartItem): string | undefined => item.seatingInfo?.seatIds.join(', ');

const itemRemoveDescription = (item: ShoppingCartItem): string =>
  $gettext('Remove all %{item_name} items from cart.', { item_name: item.name ?? '' });

const setTicketAmount = async (ticket: ShoppingCartItem, num: number) => {
  try {
    await checkoutStore.setTicketCount(ticket.itemId, num);
  } catch (e) {
    if (isVPError(e)) {
      for (const msg of e.errors) {
        toast.error(msg);
      }

      return;
    }

    throw e;
  }
};
const addTicketAmount = async (ticket: ShoppingCartItem, delta: number) =>
  setTicketAmount(ticket, (ticket.count || 0) + delta);

const onTicketRemove = async (item: ShoppingCartItem) => await checkoutStore.setTicketCount(item.itemId, 0);
const toggleTaxesOpen = () => {
  taxesOpen.value = !taxesOpen.value;
};

// Reactive state
const taxesOpen = ref(false);
const changesDisabled = computed(() => checkoutStore._shoppingCartLoading);
const disabled = computed(() => checkoutStore._shoppingCartLoading);
</script>
